import { booleanAttribute, ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { SortEvent } from 'primeng/api';
import { TablePageEvent } from 'primeng/table';
import { map, Observable, of } from 'rxjs';
import { HisAuthService } from '../../../../auth/service/his-auth.service';
import { HisOptionXtd } from '../../../../core/model/his-option';
import { HisSessionStorageKeys } from '../../../../core/model/his-session-storage-keys';
import { HisUserProfileService } from '../../../../core/service/backend/his-userprofile.service';
import { getHashCode } from '../../../../core/utils/his-string.utils';
import { HisTableConfigConstants } from '../../../../table/model/table-config-aspects';

type SortFunction = ( sortEvent: SortEvent ) => void;

@Component( {
    selector: 'his-multiselect-selection-dialog',
    templateUrl: './his-multiselect-selection-dialog.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
} )
export class HisMultiselectSelectionDialogComponent {
    protected readonly DEFAULT_ROWS_PER_PAGE = 10;
    private readonly authService = inject( HisAuthService );
    private readonly userProfileService = inject( HisUserProfileService );

    /** The label for the overlay and table header of the column displaying the option labels */
    readonly label = input( '' );
    /** Disables all ui elements  */
    readonly disabled = input( false, { transform: booleanAttribute } );
    /** Toggle the button to open the selection dialog */
    readonly showSelectButton = input( true );
    /** Toggle the 'remove all' button */
    readonly showDeleteButton = input( true );
    /** Sets a custom icon and omits the button text, creating a custom 'icon only' button to open the dialog */
    readonly selectButtonImage = input<string | undefined>();
    /** The tooltip to display for the 'icon only' button. (Requires `selectButtonImage` to be set) */
    readonly selectButtonTooltip = input<string | undefined>();
    /** Enables the use of a filter for the selectable options */
    readonly enableTableFilter = input( true );
    /** Custom function to sort the records in the table by */
    readonly customSortFunction = input<SortFunction | undefined>();

    /** The options from which the user may select from */
    readonly optionList = input<HisOptionXtd[]>( [] );
    /** The options which were previously selected already */
    readonly selectedOptions = input<HisOptionXtd[]>( [] );
    /** The updated selection after submitting the overlay */
    readonly selectedOptionsChange = output<HisOptionXtd[]>();
    
    /** The maximum rows a user may display on each page */
    readonly maxRows = input( 300 );
    /** The default number of rows that should be displayed if there is now custom user setting stored */
    readonly resultRowsPerPage = input( this.DEFAULT_ROWS_PER_PAGE );
    
    /** Overlay display state */
    protected readonly visible = signal( false );
    /** The currently displayed rows on each page */
    protected readonly rowsPerPage = signal( this.resultRowsPerPage() );
    /** The options from which the user may select from after applying the `filter` */
    protected readonly filteredOptions = signal<HisOptionXtd[]>( [] );
    /** Calculated total count of options, based on the filtered option list */
    protected readonly totalRowCount = computed( () => this.filteredOptions().length );
    /** Total number of pages in the paginator */
    protected readonly totalNumberOfPages = computed( () => Math.ceil( this.totalRowCount() / this.rowsPerPage() ) );
    /** The current page selected in the paginator */
    protected readonly currentPageNumber = computed( () => 1 + this.firstResultRow() / this.rowsPerPage() );
    /** The first option to be displayed on the current page. Dynamically determined by the pagesize and current page index from the p-table. */
    protected readonly firstResultRow = signal( 0 );
    /** i18n key used in the paginator */
    protected readonly i18nKeyForRecords = computed( () => 'de.his.faces.tags.table.datascroller.numRowsFound' + ( this.totalRowCount() === 1 ? '.record' : '.records' ) );

    private readonly tableIdHash = getHashCode( window.location.pathname + this.label() );
    private readonly pageSizeUserProfileKey = this.tableIdHash + HisTableConfigConstants.PAGE_SIZE;
    private readonly pageSizeSessionStorageKey = this.tableIdHash + HisSessionStorageKeys.CS_SYS_ANONYMOUS_PAGE_SIZE;

    /** Internal indepedent selection tracker tied to the p-table */
    protected selection: HisOptionXtd[];
    /** The filter string tied to the inputs ngModel */
    protected filter?: string;

    /**
     * Opens the overlay and initializes the options and current selection from the parent component. Once opened,
     * external changes to these inputs have no effect on the table.
     */
    public openOverlay( filterPreset?: string ) {
        this.selection = this.selectedOptions() || [];
        this.filter = this.enableTableFilter() ? filterPreset : undefined;
        this.applyFilter();
        this.getRowsPerPage().subscribe( size => {
            this.firstResultRow.set( 0 ); //reset to page one on open
            this.rowsPerPage.set( size );
            this.visible.set( true );
        } );
    }

    /**
     * Closes the overlay and removes the entire dialog from the DOM to prevent unnecessary operations on invisible elements.
     */
    public closeOverlay() {
        this.visible.set( false );
    }

    /** Removes the entire selection. */
    protected unselectAll() {
        this.selectedOptionsChange.emit( [] );
    }

    /** Submit the current selection to the parent component and close the overlay. */
    protected submitSelection() {
        this.selectedOptionsChange.emit( [...this.selection] );
        this.closeOverlay();
    }

    /**
     * Handler for the `onPage` event of the p-table paginator.
     */
    protected onPage( event: TablePageEvent ) {
        this.firstResultRow.set( event.first );
    }

    /**
     * Handler that runs when the `rowsPerPageChange` event of the `his-rows-per-page` component fires.
     * @note this function is a deliberate abstraction between `setRowsPerPage` and the template to guard against future changes.
     */
    protected onRowsPerPageChange( value: number ) {
        this.setRowsPerPage( value );
    }

    /** Applies the `filter` to the `optionList`. */
    protected applyFilter() {
        const options = [...this.optionList()];
        const filter = this.filter?.trim().toLowerCase();
        if ( !filter ) {
            this.filteredOptions.set( options );
            return;
        }
        const filterParts = filter.split( ' ' ).map( part => part.trim() ).filter( part => part.length > 0 );
        this.filteredOptions.set( options.filter( option => filterParts.every( part => option.label.toLowerCase().includes( part ) ) ) );
    }

    /**
     * Retrieves the `rowsPerPage`, either from the sessionStorage for anonymous users or the userProfile. (Defaults to `resultRowsPerPage`)
     * @note this function should be generalized in a 'table-util' or by a generic 'his-table' component.
     */
    private getRowsPerPage(): Observable<number> {
        if ( this.authService.isAnonymous ) {
            return of( +( sessionStorage.getItem( this.pageSizeSessionStorageKey ) ?? this.resultRowsPerPage() ) );
        }

        const pageSize = this.userProfileService.getValueForKey( this.pageSizeUserProfileKey );
        return pageSize.pipe(
            map( size => size !== null ? +size : this.resultRowsPerPage() )
        );
    }

    /**
     * Sets the new value for the `rowsPerPage`, either in the sessionStorage for anonymous users or the userProfile. The value is removed if it matches the default.
     * @note this function should be generalized in a 'table-util' or by a generic 'his-table' component.
     */
    private setRowsPerPage( value: number ) {
        this.rowsPerPage.set( value );
        if ( !this.authService.isAnonymous ) {
            this.userProfileService.setNewValue( this.pageSizeUserProfileKey, String( value ), String( this.DEFAULT_ROWS_PER_PAGE ) ).subscribe();
            return;
        }
        if ( this.rowsPerPage() === this.DEFAULT_ROWS_PER_PAGE ) {
            sessionStorage.removeItem( this.pageSizeSessionStorageKey );
            return;
        }
        sessionStorage.setItem( this.pageSizeSessionStorageKey, String( value ) );
    }
}
